import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";
import type { AdminPublic } from "shared";
import {
	fetchMe,
	loginWithPassword,
	loginWithToken,
	logout as logoutApi,
} from "@/api/auth";

interface SessionContextValue {
	admin: AdminPublic | null;
	isLoading: boolean;
	isAuthenticated: boolean;
	refresh: () => Promise<void>;
	loginPassword: (email: string, password: string) => Promise<void>;
	loginToken: (token: string) => Promise<void>;
	logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
	const [admin, setAdmin] = useState<AdminPublic | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	const refresh = useCallback(async () => {
		try {
			const { admin: me } = await fetchMe();
			setAdmin(me);
		} catch {
			setAdmin(null);
		}
	}, []);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			setIsLoading(true);
			await refresh();
			if (!cancelled) setIsLoading(false);
		})();
		return () => {
			cancelled = true;
		};
	}, [refresh]);

	const loginPassword = useCallback(
		async (email: string, password: string) => {
			const { admin: a } = await loginWithPassword(email, password);
			setAdmin(a);
		},
		[],
	);

	const loginToken = useCallback(async (token: string) => {
		const { admin: a } = await loginWithToken(token);
		setAdmin(a);
	}, []);

	const logout = useCallback(async () => {
		try {
			await logoutApi();
		} catch {
			/* still clear */
		}
		setAdmin(null);
	}, []);

	const value = useMemo<SessionContextValue>(
		() => ({
			admin,
			isLoading,
			isAuthenticated: !!admin,
			refresh,
			loginPassword,
			loginToken,
			logout,
		}),
		[admin, isLoading, refresh, loginPassword, loginToken, logout],
	);

	return (
		<SessionContext.Provider value={value}>{children}</SessionContext.Provider>
	);
}

export function useSession(): SessionContextValue {
	const ctx = useContext(SessionContext);
	if (!ctx) throw new Error("useSession must be used within SessionProvider");
	return ctx;
}
